import {PayloadAction, createAsyncThunk, createSlice} from "@reduxjs/toolkit";
import {ErrorPayload, supportWebformApi} from "webform/api";
import {SupportContactFormService} from "webform/gen/supportContactForm";
import {ErrorType} from "config/errors";
import {getSessionState} from "utils/getSessionState";

interface ConfigState {
    locale: string;
    isLoading?: boolean;
    isAvailable?: boolean;
    error?: ErrorType;
    userInformation?: SupportContactFormService.UserInformation;
}

export const assertWebformAvailable = createAsyncThunk(
    "webform/assertWebformAvailable",
    async (_, {rejectWithValue}) => {
        try {
            return await supportWebformApi.assertWebformAvailable({});
        } catch (error) {
            return rejectWithValue(error);
        }
    },
);

const sessionState = getSessionState();
const locale = sessionState?.locale || navigator.language;

const queryString = window.location.search;
const urlParams = new URLSearchParams(queryString);
const localeParam = urlParams.get("locale");

export const initialState: ConfigState = {
    locale: localeParam || locale,
    isLoading: true,
    isAvailable: false,
};

export const slice = createSlice({
    name: "config",
    initialState,
    reducers: {
        setLocale: (state, action: PayloadAction<string>) => {
            state.locale = action.payload;
        },
    },
    extraReducers: (builder) => {
        builder.addCase(assertWebformAvailable.fulfilled, (state, action) => {
            state.userInformation = action.payload.user_information;
            state.isLoading = false;
            state.isAvailable = action.payload.is_available;
        });
        builder.addCase(assertWebformAvailable.rejected, (state, action) => {
            state.isLoading = false;
            const typedPayload = action.payload as ErrorPayload;

            if (typedPayload.response?.error_data?.expiredAt) {
                state.error = ErrorType.TOKEN_EXPIRED;
            } else {
                state.error = ErrorType.ASSERT_WEBFORM_AVAILABLE_FETCH_FAILED;
            }
        });
        builder.addCase(assertWebformAvailable.pending, (state) => {
            state.isLoading = true;
            state.isAvailable = false;
            state.error = undefined;
        });
    },
});

export const {setLocale} = slice.actions;

export default slice.reducer;
